import { LayersOutlined } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { JobStatus, PartFinderActivityEntry, PartFinderBatch, PartFinderReport } from '@/api/generatedApi';
import { FailureWhyButton } from '@/components/FailureDiagnostics/FailureWhyButton';
import { getBatchLabel } from '@/components/PartFinder/batchLabel';
import { PartFinderBatchThumbnail } from '@/components/PartFinder/PartFinderBatchThumbnail';
import { PartFinderThumbnailCell } from '@/components/PartFinder/PartFinderThumbnailCell';
import { THUMBNAIL_SIZE } from '@/components/PartFinder/PartFinderThumbnailHover';
import { StatusBadge } from '@/components/StatusBadge';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTenants } from '@/services/tenants/useTenants';
import { CreatedCell, CreatorCell, EllipsisText, Row, RowLink, StatusCell, WhyControl } from './RecentRow';

// The part finder page leads with the part number and follows it with what the lookup resolved to.
// The tile keeps that order and drops the columns it has no room for (top recommendation).
const NameCell = styled(EllipsisText)({
  flex: 1.8,
  minWidth: 0,
  fontWeight: 600,
});

// Without the thumbnail column there is nowhere to put the stack mark, so it rides beside the
// batch name instead and a bulk search stays tellable from a single lookup either way.
const BatchNameCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: 1.8,
  minWidth: 0,
  // Matches the part number column beside it.
  fontWeight: 600,
  '& svg': {
    fontSize: '16px',
    flexShrink: 0,
  },
});

const DetailCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1.6,
  minWidth: 0,
  overflow: 'hidden',
});

// The thumbnail gutter takes width the reports tile does not spend, so this column claims some of
// it back rather than ellipsising every address to "test@physna.c...".
const WideCreatorCell = styled(CreatorCell)({
  flex: 1.6,
});

// This tile carries two fewer columns than the reports tile, so it can afford the full
// "less than a minute ago" rather than ellipsising a just-started lookup to "less than a minu...".
const WideCreatedCell = styled(CreatedCell)({
  width: '164px',
});

// The tile holds this gutter on every row, including lookups that found no image, so part numbers
// stay aligned down the column. Sizing it squarely also holds every row to one height, rather than
// letting rows with a picture stand taller than rows without.
//
// It lifts above the row link's stretched hit area for the same reason the Why control does: left
// underneath, the overlay swallows the hover and the preview the part finder page gives never
// appears. Clicks still open the row, since the cell is inside the link either way.
const ThumbnailCell = styled(Box)({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
});

const describeBatch = (batch: PartFinderBatch) => {
  const { total, completed, failed } = batch.breakdown;
  const progress = `${completed.toLocaleString()} of ${total.toLocaleString()} complete`;
  return failed > 0 ? `${progress}, ${failed.toLocaleString()} failed` : progress;
};

const ReportCells = ({ report, showThumbnail }: { report: PartFinderReport; showThumbnail: boolean }) => (
  <>
    {showThumbnail && (
      <ThumbnailCell>
        <PartFinderThumbnailCell report={report} />
      </ThumbnailCell>
    )}
    <NameCell>{report.partNumber}</NameCell>
    <DetailCell>
      <EllipsisText color='text.secondary'>{report.nsnReport?.description || report.description}</EllipsisText>
    </DetailCell>
  </>
);

const BatchCells = ({ batch, showThumbnail }: { batch: PartFinderBatch; showThumbnail: boolean }) => (
  <>
    {showThumbnail && (
      <ThumbnailCell>
        <PartFinderBatchThumbnail batch={batch} />
      </ThumbnailCell>
    )}
    <BatchNameCell>
      {!showThumbnail && <LayersOutlined />}
      <EllipsisText>{getBatchLabel(batch)}</EllipsisText>
    </BatchNameCell>
    <DetailCell>
      <EllipsisText color='text.secondary'>{describeBatch(batch)}</EllipsisText>
    </DetailCell>
  </>
);

export const RecentPartFinderActivityRow = ({ entry }: { entry: PartFinderActivityEntry }) => {
  const { getPath } = useTenants();
  const { isPartFinderThumbnailsEnabled } = useFeatureFlags();

  const isBatch = entry.type === 'batch';
  const subject = isBatch ? entry.batch : entry.report;
  const name = isBatch ? getBatchLabel(entry.batch) : entry.report.partNumber;
  const to = isBatch ? getPath(`part-finder/batches/${entry.batch.id}`) : getPath(`part-finder-reports/${subject.id}`);

  return (
    <Row data-testid={`recent-part-finder-${entry.type}-row`}>
      <RowLink to={to} title={`View ${name}`}>
        {entry.type === 'batch' ? (
          <BatchCells batch={entry.batch} showThumbnail={isPartFinderThumbnailsEnabled} />
        ) : (
          <ReportCells report={entry.report} showThumbnail={isPartFinderThumbnailsEnabled} />
        )}
        <WideCreatorCell color='text.secondary'>{subject.creator?.email}</WideCreatorCell>
        <WideCreatedCell color='text.secondary'>
          {formatDistanceToNow(new Date(subject.createdAt), { addSuffix: true })}
        </WideCreatedCell>
      </RowLink>
      <StatusCell>
        <StatusBadge
          status={subject.status}
          progress={subject.progress}
          data-testid={`part-finder-status-${subject.status}`}
        />
        {!isBatch && subject.status === JobStatus.FAILED && (
          <WhyControl>
            <FailureWhyButton assetId={subject.id} assetName={name} kind='part-finder-report' />
          </WhyControl>
        )}
      </StatusCell>
    </Row>
  );
};
